import { Link } from 'react-router-dom';
import PageMeta from '../../components/SEO/PageMeta';
import { SITE_BRAND, SITE_NAME } from '../../config/site';
import styles from './LegalPage.module.css';

export default function AboutUsPage() {
  return (
    <>
      <PageMeta
        title="About Us"
        description="Learn about SaveItPro — a free, privacy-friendly online toolkit for media conversion, text and JSON utilities, JavaScript tools, array tools, and time calculations."
        path="/about-us"
      />

      <article className={styles.page}>
        <header className={styles.hero}>
          <h1>About Us</h1>
          <p>
            {SITE_NAME} is a free, browser-based toolkit built for creators, developers, and everyday
            users who need quick, reliable utilities without installing software or signing up.
          </p>
        </header>

        <section className={styles.section}>
          <h2>Our mission</h2>
          <p>
            We started {SITE_BRAND} with a simple goal: make the small, repetitive tasks of digital
            work effortless. Whether you are converting an image for a blog post, reformatting text,
            cleaning up JSON, experimenting with JavaScript, or calculating how long a call lasted,
            you should be able to do it in seconds — for free, in your browser, on any device.
          </p>
          <p>
            Everything we build is designed to be fast, distraction-free, and respectful of your
            privacy. Most of our tools run right in your browser, so your data stays with you.
          </p>
        </section>

        <section className={styles.section}>
          <h2>What we offer</h2>
          <p>
            {SITE_NAME} brings together a growing collection of utilities under one roof. Here is what
            you can do today:
          </p>
          <ul>
            <li>
              <strong>Image &amp; media converters</strong> — Convert between popular image formats
              such as JPEG, PNG, WebP, and more with a clean, drag-and-drop workflow.{' '}
              <Link to="/image-converters">Browse all converters</Link>.
            </li>
            <li>
              <strong>String &amp; text tools</strong> — Change case (upper, lower, title, sentence,
              camel, Pascal, snake, kebab), reverse text, trim and remove spaces, count characters and
              words, and compare two strings with highlighted differences.{' '}
              <Link to="/string-tools">Open string tools</Link>.
            </li>
            <li>
              <strong>Array tools</strong> — Transform, clean, and compare arrays with developer-focused
              utilities. <Link to="/array-tools">Open array tools</Link>.
            </li>
            <li>
              <strong>JavaScript object &amp; JSON tools</strong> — Format, transform, and compare
              JSON objects with color-coded added, removed, and changed summaries.{' '}
              <Link to="/javascript-object-tools">Open object tools</Link>.
            </li>
            <li>
              <strong>Online JavaScript terminal</strong> — Run JavaScript in a browser sandbox with
              command history, autocomplete, and clear output.{' '}
              <Link to="/javascript-terminal">Open the terminal</Link>.
            </li>
            <li>
              <strong>Online JavaScript editor</strong> — Write and run code in a split editor with
              syntax highlighting, line numbers, formatting, and live console output.{' '}
              <Link to="/online-javascript-editor">Open the editor</Link>.
            </li>
            <li>
              <strong>Time tools</strong> — Compare two times across time zones to see the total in
              hours, minutes, and seconds, view the live current time for any UTC offset, and track
              multiple intervals to get a grand total. <Link to="/time-tools">Open time tools</Link>.
            </li>
            <li>
              <strong>Blog</strong> — Guides, tips, and updates organized by category to help you get
              the most out of our tools. <Link to="/blogs">Read the blog</Link>.
            </li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>Why people choose {SITE_BRAND}</h2>
          <ul>
            <li>
              <strong>Free to use.</strong> No subscriptions or paywalls for our core tools.
            </li>
            <li>
              <strong>No sign-up required.</strong> Open a tool and start working immediately.
            </li>
            <li>
              <strong>Privacy-friendly.</strong> Many tools run entirely in your browser, so your
              content does not need to leave your device.
            </li>
            <li>
              <strong>Works everywhere.</strong> A responsive design that runs on desktop, tablet, and
              mobile.
            </li>
            <li>
              <strong>Always improving.</strong> We regularly add new tools and refine existing ones
              based on real feedback.
            </li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>Get in touch</h2>
          <p>
            We love hearing from the people who use our tools. If you have a feature request, found a
            bug, or want to partner with us, reach out any time through our{' '}
            <Link to="/contact-us">Contact Us</Link> page. Your feedback directly shapes what we build
            next.
          </p>
        </section>
      </article>
    </>
  );
}
